/**
 * JvDeckungsbeitragPage — JV-Deckungsbeitrag Analyse fuer Joint-Venture-Standorte.
 *
 * Zeigt die WeShip-Profitabilitaet am JV-Standort nach RevShare-Abgabe.
 * Kosten = RevShare (FC-Anteil) + Material + Carrier. Keine Personalkosten.
 *
 * @page /jv-deckungsbeitrag
 * @roles admin only (jv_partner hat KEINEN Zugriff)
 * @api GET /api/jv-deckungsbeitrag/uebersicht
 * @api GET /api/jv-deckungsbeitrag/kunde/:id
 * @api GET /api/jv-deckungsbeitrag/charts
 * @i18n jvDb
 * @since v2.185.0
 */
import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getDateRange } from '../utils/dateUtils';
import DateRangeFilter from '../components/DateRangeFilter';
import { Info, Search, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Card, KpiCard, Badge, Button, PageHeader } from '../components/ui';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell, BarChart,
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

/* ─── Helpers ─────────────────────────────────────────── */
function fmt(n, d = 0) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtEur(n) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtEurCompact(n) {
  if (n == null) return { text: '–', full: null };
  const v = Number(n);
  const abs = Math.abs(v);
  const full = fmtEur(v);
  if (abs >= 1000000) return { text: (v / 1000000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M €', full };
  if (abs >= 10000) return { text: (v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K €', full };
  if (abs >= 1000) return { text: Math.round(v).toLocaleString('de-DE') + ' €', full };
  return { text: full, full: null };
}
function fmtDate(d) {
  if (!d) return '–';
  const s = String(d);
  const dt = s.includes('T') ? new Date(s) : new Date(s.slice(0, 10) + 'T12:00:00');
  return `${dt.getDate().toString().padStart(2, '0')}.${(dt.getMonth() + 1).toString().padStart(2, '0')}.${String(dt.getFullYear()).slice(2)}`;
}

function margeBadge(pct) {
  if (pct >= 20) return 'bg-emerald-500/15 text-emerald-600';
  if (pct >= 10) return 'bg-yellow-500/15 text-yellow-600';
  return 'bg-red-500/15 text-red-600';
}

const CHART_COLORS = ['#338edc', '#22D3EE', '#6366F1', '#94A3B8', '#A78BFA', '#34d399'];

/* ─── Main Component ──────────────────────────────────── */
export default function JvDeckungsbeitragPage() {
  const { user } = useAuth();
  const { t } = useLanguage();

  // Date filter
  const init = getDateRange('monat');
  const [von, setVon] = useState(init.von);
  const [bis, setBis] = useState(init.bis);
  const [preset, setPreset] = useState('monat');

  // Data states
  const [data, setData] = useState(null);
  const [chartsData, setChartsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [tab, setTab] = useTabParam('kunden');

  // Kunden-expand + detail
  const [expandedKunde, setExpandedKunde] = useState(null);
  const [kundeDetail, setKundeDetail] = useState(null);
  const [kundeDetailLoading, setKundeDetailLoading] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  // Search + sort
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('umsatz');
  const [sortDir, setSortDir] = useState('desc');

  // Recalc
  const [recalcing, setRecalcing] = useState(false);

  const handleDateChange = useCallback(({ von: v, bis: b, preset: p }) => {
    setVon(v); setBis(b); setPreset(p);
    setExpandedKunde(null); setKundeDetail(null); setExpandedOrder(null);
  }, []);

  // Fetch uebersicht
  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    fetch(`${API}/api/jv-deckungsbeitrag/uebersicht?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [von, bis]);

  // Fetch charts
  useEffect(() => {
    if (!von || !bis || tab !== 'charts') return;
    setChartsLoading(true);
    fetch(`${API}/api/jv-deckungsbeitrag/charts?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) setChartsData(d); })
      .catch(() => {})
      .finally(() => setChartsLoading(false));
  }, [von, bis, tab]);

  // Fetch Kunde detail on expand
  useEffect(() => {
    if (!expandedKunde) { setKundeDetail(null); return; }
    setKundeDetailLoading(true);
    setExpandedOrder(null);
    fetch(`${API}/api/jv-deckungsbeitrag/kunde/${expandedKunde}?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!d.error) setKundeDetail(d); })
      .catch(() => {})
      .finally(() => setKundeDetailLoading(false));
  }, [expandedKunde, von, bis]);

  // Recalculate
  const handleRecalc = async () => {
    setRecalcing(true);
    try {
      await fetch(`${API}/api/jobs/deckungsbeitrag/berechnen`, { method: 'POST', credentials: 'include' });
    } catch {}
    setTimeout(() => { setRecalcing(false); }, 2000);
  };

  // Filter + sort kunden
  const filteredKunden = useMemo(() => {
    if (!data?.kunden) return [];
    let list = data.kunden;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(k => k.kunde_name.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const va = a[sortCol] ?? 0, vb = b[sortCol] ?? 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [data, search, sortCol, sortDir]);

  // Aggregate RevShare per category (for RevShare-Analyse tab)
  const revshareByCategory = useMemo(() => {
    if (!kundeDetail && !data) return [];
    // We need to aggregate all order handling_details across all customers
    // This requires loading each customer - for now, show per-expanded-customer
    if (!kundeDetail?.auftraege) return [];
    const catMap = {};
    for (const o of kundeDetail.auftraege) {
      for (const pos of (o.handling_detail || [])) {
        const key = pos.name || 'Sonstige';
        if (!catMap[key]) catMap[key] = { name: key, umsatz: 0, fc_anteil: 0, ws_anteil: 0, count: 0 };
        catMap[key].umsatz += pos.umsatz || 0;
        catMap[key].fc_anteil += pos.fc_anteil || 0;
        catMap[key].ws_anteil += (pos.umsatz || 0) - (pos.fc_anteil || 0);
        catMap[key].count += 1;
      }
    }
    return Object.values(catMap).sort((a, b) => b.umsatz - a.umsatz);
  }, [kundeDetail, data]);

  const totals = data?.totals || {};
  const partner = data?.jv_partner || 'Finecom';

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const TABS = [
    { key: 'kunden', label: t('jvDb.kundenUebersicht') + (data?.kunden ? ` (${data.kunden.length})` : '') },
    { key: 'charts', label: t('jvDb.charts') },
    { key: 'revshare', label: t('jvDb.revshareAnalyse') },
    { key: 'kpi', label: t('jvDb.kpiAnalyse') },
  ];

  return (
    <div className="p-page">
      {/* Header */}
      <PageHeader
        title={t('jvDb.title')}
        subtitle={t('jvDb.subtitle')}
        actions={
          <Button variant="secondary" size="sm" icon={RefreshCw} onClick={handleRecalc} loading={recalcing}>
            {t('cm.recalculate')}
          </Button>
        }
      />

      {/* Filter Bar */}
      <DateRangeFilter von={von} bis={bis} preset={preset} onChange={handleDateChange} className="mb-section" />

      {/* Info banner */}
      <div className="mb-section p-3 bg-blue-500/5 border border-blue-500/15 rounded-card text-sm text-[var(--text-secondary)] flex items-center gap-2">
        <Info className="w-4 h-4 text-[var(--primary)] shrink-0" />
        {t('jvDb.infoBanner', { partner })}
      </div>

      {/* KPI Bar — 2 rows */}
      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-card border border-line-card animate-pulse bg-surface-inset" />
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-card border border-line-card animate-pulse bg-surface-inset" />
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Row 1: Handling (operativ) */}
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-medium mb-2 mt-section">
            {t('jvDb.handling')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-section">
            <KpiCard label={t('jvDb.handlingUmsatz')} value={fmtEurCompact(totals.handling?.umsatz).text} tooltip={fmtEurCompact(totals.handling?.umsatz).full} />
            <KpiCard label={t('jvDb.revshareAbgabe')} value={fmtEurCompact(totals.handling?.revshare).text} tooltip={fmtEurCompact(totals.handling?.revshare).full} trend={t('jvDb.fcAnteilDesc')} />
            <KpiCard label={t('jvDb.handlingDb')} value={fmtEurCompact(totals.handling?.db).text} tooltip={fmtEurCompact(totals.handling?.db).full} trend={t('jvDb.wsAnteilDesc')} />
            <KpiCard label={t('jvDb.handlingMarge')} value={totals.auftraege > 0 ? fmt(totals.handling?.marge_pct, 1) + '%' : '—'}
              trend={totals.auftraege > 0 && totals.handling?.marge_pct != null
                ? (totals.handling.marge_pct >= 30
                  ? <span className="text-emerald-600 bg-emerald-500/15 px-2 py-0.5 rounded-full text-xs font-medium">{t('jvDb.akzeptabel')}</span>
                  : <span className="text-yellow-600 bg-yellow-500/15 px-2 py-0.5 rounded-full text-xs font-medium">{t('jvDb.kritisch')}</span>)
                : null
              }
            />
          </div>

          {/* Separator between rows */}
          <div className="border-b border-[var(--border-card,#E5E7EB)] mb-section" />

          {/* Row 2: Gesamt (inkl. Material & Porto) */}
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider font-medium mb-2">
            {t('jvDb.gesamtInkl')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-section">
            <KpiCard label={t('jvDb.gesamtUmsatz')} value={fmtEurCompact(totals.umsatz_gesamt).text} tooltip={fmtEurCompact(totals.umsatz_gesamt).full}
              trend={<span className="text-[var(--text-muted)]">{fmt(totals.auftraege)} {t('cm.orders')} · {data?.kunden?.length || 0} {t('cm.customers')}</span>} />
            <KpiCard label={t('jvDb.gesamtKosten')} value={fmtEurCompact(totals.kosten_gesamt).text} tooltip={fmtEurCompact(totals.kosten_gesamt).full} trend={t('jvDb.gesamtKostenDesc')} />
            <KpiCard label={t('jvDb.dbGesamt')} value={fmtEurCompact(totals.db_gesamt).text} tooltip={fmtEurCompact(totals.db_gesamt).full} />
            <KpiCard label={t('jvDb.margeGesamt')} value={totals.auftraege > 0 ? fmt(totals.marge_gesamt_pct, 1) + '%' : '—'}
              trend={totals.auftraege > 0 && totals.marge_gesamt_pct != null
                ? (totals.marge_gesamt_pct >= 15
                  ? <span className="text-emerald-600 bg-emerald-500/15 px-2 py-0.5 rounded-full text-xs font-medium">{t('jvDb.akzeptabel')}</span>
                  : <span className="text-red-600 bg-red-500/15 px-2 py-0.5 rounded-full text-xs font-medium">{t('jvDb.kritisch')}</span>)
                : null
              }
            />
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="mb-section">
        <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1">
          {TABS.map(tb => (
            <button key={tb.key} onClick={() => setTab(tb.key)}
              className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
                tab === tb.key
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}>
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {tab === 'kunden' && <KundenTab
        kunden={filteredKunden} totals={totals} loading={loading}
        search={search} setSearch={setSearch}
        handleSort={handleSort} sortCol={sortCol} sortDir={sortDir}
        expandedKunde={expandedKunde} setExpandedKunde={setExpandedKunde}
        kundeDetail={kundeDetail} kundeDetailLoading={kundeDetailLoading}
        expandedOrder={expandedOrder} setExpandedOrder={setExpandedOrder}
        t={t}
      />}
      {tab === 'charts' && <ChartsTab data={chartsData} loading={chartsLoading} t={t} />}
      {tab === 'revshare' && <RevShareTab
        data={data} revshareByCategory={revshareByCategory}
        expandedKunde={expandedKunde} kundeDetail={kundeDetail}
        setExpandedKunde={setExpandedKunde} setTab={setTab}
        t={t}
      />}
      {tab === 'kpi' && <KpiAnalyseTab data={data} t={t} />}
    </div>
  );
}

/* ─── Kunden Tab ──────────────────────────────────────── */
function KundenTab({ kunden, totals, loading, search, setSearch, handleSort, sortCol, sortDir, expandedKunde, setExpandedKunde, kundeDetail, kundeDetailLoading, expandedOrder, setExpandedOrder, t }) {
  if (loading) return <div className="text-center text-[var(--text-tertiary)] py-12">{t('common.loading')}</div>;

  const cols = [
    { key: 'kunde_name', label: t('cm.customer'), align: 'left' },
    { key: 'auftraege', label: t('cm.orders'), align: 'right' },
    { key: 'handling_umsatz', label: t('jvDb.handlingUmsatz'), align: 'right' },
    { key: 'handling_revshare', label: 'RevShare', align: 'right' },
    { key: 'handling_db', label: t('jvDb.handlingDb'), align: 'right' },
    { key: 'material_db', label: t('jvDb.materialDb'), align: 'right' },
    { key: 'carrier_db', label: t('jvDb.carrierDb'), align: 'right' },
    { key: 'db_gesamt', label: t('jvDb.dbGesamt'), align: 'right' },
    { key: 'marge_gesamt_pct', label: t('jvDb.margeGesamt'), align: 'right' },
  ];

  return (
    <Card padding="lg">
      {/* Search */}
      {kunden.length > 5 && (
        <div className="mb-4">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={`${t('common.search')}...`}
              className="pl-9 pr-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none w-full"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
              <th className="w-8 py-3" />
              {cols.map(c => (
                <th key={c.key} className={`py-3 cursor-pointer select-none hover:text-content-secondary transition-colors ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                  onClick={() => handleSort(c.key)}>
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sortCol === c.key && (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                      </svg>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kunden.length === 0 ? (
              <tr><td colSpan={cols.length + 1} className="py-8 text-center text-[var(--text-tertiary)]">{t('common.no_data')}</td></tr>
            ) : kunden.map(k => (
              <Fragment key={k.kunde_id}>
                <tr className="border-b border-line hover:bg-surface-hover cursor-pointer transition-colors"
                  onClick={() => setExpandedKunde(expandedKunde === k.kunde_id ? null : k.kunde_id)}>
                  <td className="py-2.5 px-2 text-[var(--text-tertiary)]">
                    {expandedKunde === k.kunde_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="py-2.5 px-3 font-medium text-[var(--text-primary)]">{k.kunde_name}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmt(k.auftraege)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-primary)] tabular-nums">{fmtEur(k.handling_umsatz)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(k.handling_revshare)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-primary)] tabular-nums font-medium">{fmtEur(k.handling_db)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(k.material_db)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(k.carrier_db)}</td>
                  <td className="py-2.5 px-3 text-right text-[var(--text-primary)] tabular-nums font-medium">{fmtEur(k.db_gesamt)}</td>
                  <td className="py-2.5 px-3 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${margeBadge(k.marge_gesamt_pct)}`}>
                      {fmt(k.marge_gesamt_pct, 1)}%
                    </span>
                  </td>
                </tr>
                {/* Expanded: order list */}
                {expandedKunde === k.kunde_id && (
                  <tr>
                    <td colSpan={cols.length + 1} className="p-0">
                      <KundeDetailPanel
                        detail={kundeDetail} loading={kundeDetailLoading}
                        expandedOrder={expandedOrder} setExpandedOrder={setExpandedOrder}
                        t={t}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
          {/* Footer totals */}
          {kunden.length > 0 && (
            <tfoot className="border-t-2 border-line-hover bg-surface-card">
              <tr className="font-semibold">
                <td className="py-2.5 px-2" />
                <td className="py-2.5 px-3 text-[var(--text-primary)]">{t('common.total')}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmt(totals.auftraege)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.handling?.umsatz)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.handling?.revshare)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.handling?.db)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.material?.db)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.carrier?.db)}</td>
                <td className="py-2.5 px-3 text-right tabular-nums">{fmtEur(totals.db_gesamt)}</td>
                <td className="py-2.5 px-3 text-right">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${margeBadge(totals.marge_gesamt_pct)}`}>
                    {fmt(totals.marge_gesamt_pct, 1)}%
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

/* ─── Kunde Detail Panel (expanded row) ───────────────── */
function KundeDetailPanel({ detail, loading, expandedOrder, setExpandedOrder, t }) {
  if (loading) return <div className="py-6 text-center text-[var(--text-tertiary)]">{t('common.loading')}</div>;
  if (!detail) return null;

  const orders = detail.auftraege || [];
  const summary = detail.summary || {};

  return (
    <div className="bg-[var(--bg-secondary)] border-t border-[var(--border-default)]">
      {/* Summary bar */}
      <div className="flex items-center gap-6 px-6 py-3 text-xs text-[var(--text-secondary)] border-b border-[var(--border-subtle)] flex-wrap">
        <span><strong>{fmt(summary.auftraege)}</strong> {t('cm.orders')}</span>
        <span>{t('jvDb.handlingDb')}: <strong>{fmtEur(summary.handling?.db)}</strong> ({fmt(summary.handling?.marge_pct, 1)}%)</span>
        <span>{t('jvDb.materialDb')}: <strong>{fmtEur(summary.material?.db)}</strong></span>
        <span>{t('jvDb.carrierDb')}: <strong>{fmtEur(summary.carrier?.db)}</strong></span>
        <span>{t('jvDb.dbGesamt')}: <strong>{fmtEur(summary.db_gesamt)}</strong> ({fmt(summary.marge_gesamt_pct, 1)}%)</span>
        <span>{t('cm.avg')}: <strong>{fmtEur(summary.avg_db_pro_auftrag)}</strong>/{t('cm.order')}</span>
      </div>

      {/* Order table */}
      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[var(--bg-secondary)]">
            <tr className="border-b border-[var(--border-subtle)]">
              <th className="w-6 py-2" />
              <th className="py-2 px-2 text-left font-semibold text-[var(--text-tertiary)]">{t('cm.order')}</th>
              <th className="py-2 px-2 text-left font-semibold text-[var(--text-tertiary)]">{t('cm.date')}</th>
              <th className="py-2 px-2 text-right font-semibold text-[var(--text-tertiary)]">HDL-DB</th>
              <th className="py-2 px-2 text-right font-semibold text-[var(--text-tertiary)]">MAT-DB</th>
              <th className="py-2 px-2 text-right font-semibold text-[var(--text-tertiary)]">CARRIER-DB</th>
              <th className="py-2 px-2 text-right font-semibold text-[var(--text-tertiary)]">{t('jvDb.dbGesamt')}</th>
              <th className="py-2 px-2 text-right font-semibold text-[var(--text-tertiary)]">DB%</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <Fragment key={o.picking_name}>
                <tr className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] cursor-pointer"
                  onClick={() => setExpandedOrder(expandedOrder === o.picking_name ? null : o.picking_name)}>
                  <td className="py-1.5 px-1 text-[var(--text-tertiary)]">
                    {expandedOrder === o.picking_name ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[var(--text-primary)]">
                    {o.picking_name}
                    {o.is_sop && <span className="ml-1 px-1 py-0.5 rounded text-[9px] bg-cyan-500/15 text-cyan-600 font-medium">SOP</span>}
                  </td>
                  <td className="py-1.5 px-2 text-[var(--text-secondary)]">{fmtDate(o.datum)}</td>
                  <td className="py-1.5 px-2 text-right text-[var(--text-primary)] tabular-nums font-medium">{fmtEur(o.handling_db)}</td>
                  <td className="py-1.5 px-2 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(o.material_db)}</td>
                  <td className="py-1.5 px-2 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(o.carrier_db)}</td>
                  <td className="py-1.5 px-2 text-right text-[var(--text-primary)] tabular-nums font-medium">{fmtEur(o.db_gesamt)}</td>
                  <td className="py-1.5 px-2 text-right">
                    <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium ${margeBadge(o.marge_gesamt_pct)}`}>
                      {fmt(o.marge_gesamt_pct, 1)}%
                    </span>
                  </td>
                </tr>
                {/* Expanded order: RevShare breakdown */}
                {expandedOrder === o.picking_name && (
                  <tr>
                    <td colSpan={9} className="p-0">
                      <OrderBlockDetail order={o} t={t} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Order Block Detail (3 blocks: Handling/Material/Carrier) ── */
function OrderBlockDetail({ order, t }) {
  const handlingPos = order.handling_detail || [];
  const materialPos = order.material_detail || [];
  const carrierPos = order.carrier_detail || [];

  const BlockTable = ({ label, rows, costLabel, sumUmsatz, sumKosten, sumDb, margePct }) => (
    <div className="mb-3">
      <div className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">{label}</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--border-default)]">
            <th className="py-1 text-left font-semibold text-[var(--text-tertiary)]">{t('jvDb.position')}</th>
            <th className="py-1 text-right font-semibold text-[var(--text-tertiary)]">{t('cm.revenue')}</th>
            <th className="py-1 text-right font-semibold text-[var(--text-tertiary)]">{costLabel}</th>
            <th className="py-1 text-right font-semibold text-[var(--text-tertiary)]">DB</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--border-subtle)]">
              <td className="py-0.5 text-[var(--text-primary)]">{r.name}</td>
              <td className="py-0.5 text-right tabular-nums text-[var(--text-secondary)]">{fmtEur(r.umsatz)}</td>
              <td className="py-0.5 text-right tabular-nums text-[var(--text-secondary)]">{fmtEur(r.kosten != null ? r.kosten : r.fc_anteil)}</td>
              <td className="py-0.5 text-right tabular-nums text-[var(--text-primary)]">{fmtEur(r.db != null ? r.db : ((r.umsatz || 0) - (r.fc_anteil || 0)))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-[var(--border-default)] font-semibold">
            <td className="py-1 text-[var(--text-primary)]">&Sigma;</td>
            <td className="py-1 text-right tabular-nums">{fmtEur(sumUmsatz)}</td>
            <td className="py-1 text-right tabular-nums">{fmtEur(sumKosten)}</td>
            <td className="py-1 text-right tabular-nums">{fmtEur(sumDb)} <span className="text-[var(--text-muted)] font-normal">({fmt(margePct, 1)}%)</span></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  // Handling rows: map from revshare detail format
  const hdlRows = handlingPos.map(p => ({
    name: p.name, umsatz: p.umsatz, kosten: p.fc_anteil, db: (p.umsatz || 0) - (p.fc_anteil || 0),
  }));

  return (
    <div className="bg-[var(--bg-inset)] px-8 py-4 border-t border-[var(--border-subtle)]">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BlockTable label="HANDLING" rows={hdlRows} costLabel="RevShare"
          sumUmsatz={order.handling_umsatz} sumKosten={order.handling_revshare} sumDb={order.handling_db}
          margePct={order.handling_marge_pct} />
        <BlockTable label="MATERIAL" rows={materialPos} costLabel={t('jvDb.einkauf')}
          sumUmsatz={order.material_umsatz} sumKosten={order.material_einkauf} sumDb={order.material_db}
          margePct={order.material_umsatz > 0 ? ((order.material_db / order.material_umsatz) * 100) : 0} />
        <BlockTable label="CARRIER" rows={carrierPos} costLabel={t('jvDb.einkauf')}
          sumUmsatz={order.carrier_umsatz} sumKosten={order.carrier_einkauf} sumDb={order.carrier_db}
          margePct={order.carrier_umsatz > 0 ? ((order.carrier_db / order.carrier_umsatz) * 100) : 0} />
      </div>

      {/* Gesamt footer */}
      <div className="mt-3 pt-3 border-t-2 border-[var(--border-default)] flex items-center gap-6 text-xs font-semibold">
        <span className="text-[var(--text-primary)]">GESAMT</span>
        <span className="tabular-nums">{t('cm.revenue')}: {fmtEur(order.umsatz_gesamt)}</span>
        <span className="tabular-nums">{t('cm.costs')}: {fmtEur(order.kosten)}</span>
        <span className="tabular-nums">DB: {fmtEur(order.db_gesamt)}</span>
        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] ${margeBadge(order.marge_gesamt_pct)}`}>
          {fmt(order.marge_gesamt_pct, 1)}%
        </span>
      </div>

      {/* Meta info */}
      <div className="mt-2 text-[10px] text-[var(--text-muted)] flex gap-4 flex-wrap">
        {order.carrier && <span>Carrier: {order.carrier}</span>}
        {order.picker_name && <span>Picker: {order.picker_name}</span>}
        {order.packer_name && <span>Packer: {order.packer_name}</span>}
        {order.skus != null && <span>SKUs: {order.skus}</span>}
        {order.artikel != null && <span>Art.: {order.artikel}</span>}
      </div>
    </div>
  );
}

/* ─── Charts Tab ──────────────────────────────────────── */
function ChartsTab({ data, loading, t }) {
  if (loading || !data) return <div className="text-center text-[var(--text-tertiary)] py-12">{t('common.loading')}</div>;

  const trend = data.monthly_trend || [];
  const kundenAnteil = data.kunden_anteil || [];

  // DB composition stacked bar data
  const dbCompData = trend.map(m => ({
    monat: m.monat?.slice(5) || '',
    [t('jvDb.handlingDb')]: m.handling_db || 0,
    [t('jvDb.materialDb')]: m.material_db || 0,
    [t('jvDb.carrierDb')]: m.carrier_db || 0,
  }));

  // Stacked costs data
  const costData = trend.map(m => ({
    monat: m.monat?.slice(5) || '',
    RevShare: m.revshare || 0,
    Material: m.kosten_material || 0,
    Carrier: m.kosten_carrier || 0,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Chart 1: Umsatz & DB Trend with dual marge */}
      <Card className="lg:col-span-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.margeTrend')}</h3>
        {trend.length < 2 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('cm.not_enough_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trend} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="monat" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => v?.slice(5) || ''} />
              <YAxis yAxisId="left" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => `${v}%`} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v, name) => {
                  if (name === t('jvDb.handlingMargePct') || name === t('jvDb.gesamtMargePct')) return [`${fmt(v, 1)}%`, name];
                  return [fmtEur(v), name];
                }} />
              <Legend />
              <Bar yAxisId="left" dataKey="umsatz" name={t('cm.revenue')} fill="#338edc" radius={[4, 4, 0, 0]} barSize={24} opacity={0.3} />
              <Bar yAxisId="left" dataKey="db_gesamt" name={t('jvDb.dbGesamt')} fill="#338edc" radius={[4, 4, 0, 0]} barSize={24} />
              <Line yAxisId="right" dataKey="handling_marge_pct" name={t('jvDb.handlingMargePct')} stroke="#338edc" strokeWidth={2} dot={{ r: 3, fill: '#338edc' }} />
              <Line yAxisId="right" dataKey="marge_gesamt_pct" name={t('jvDb.gesamtMargePct')} stroke="#6366F1" strokeDasharray="5 5" strokeWidth={2} dot={{ r: 3, fill: '#6366F1' }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Chart 2: DB-Zusammensetzung (Stacked Bar) */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.dbZusammensetzung')}</h3>
        {dbCompData.length < 2 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('cm.not_enough_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={dbCompData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="monat" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v) => fmtEur(v)} />
              <Legend />
              <Bar dataKey={t('jvDb.handlingDb')} stackId="a" fill="#338edc" radius={[0, 0, 0, 0]} />
              <Bar dataKey={t('jvDb.materialDb')} stackId="a" fill="#22D3EE" />
              <Bar dataKey={t('jvDb.carrierDb')} stackId="a" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Chart 3: Kunden-Anteil (Donut) */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.kundenAnteil')}</h3>
        {kundenAnteil.length === 0 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('common.no_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={kundenAnteil} dataKey="umsatz" nameKey="kunde_name" cx="50%" cy="50%"
                innerRadius={60} outerRadius={100} paddingAngle={2}>
                {kundenAnteil.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <ReTooltip formatter={(v, name) => [fmtEur(v), name]}
                contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }} />
              <Legend formatter={(value) => <span className="text-xs text-[var(--text-secondary)]">{value}</span>} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Chart 4: Kosten-Aufschlüsselung (Stacked Bar) */}
      <Card className="lg:col-span-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.kostenstruktur')}</h3>
        {costData.length < 2 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('cm.not_enough_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={costData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="monat" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v) => fmtEur(v)} />
              <Legend />
              <Bar dataKey="RevShare" stackId="a" fill="#338edc" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Material" stackId="a" fill="#22D3EE" />
              <Bar dataKey="Carrier" stackId="a" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

/* ─── RevShare Analyse Tab ────────────────────────────── */
function RevShareTab({ data, revshareByCategory, expandedKunde, kundeDetail, setExpandedKunde, setTab, t }) {
  const kunden = (data?.kunden || []).map(k => ({
    ...k,
    ws_anteil_calc: Math.max(0, (k.umsatz_handling || 0) - (k.revshare_abgabe || 0)),
  }));

  // If no customer expanded, show per-customer RevShare table
  // If customer expanded & detail loaded, show per-category breakdown
  const hasDetail = expandedKunde && kundeDetail;

  return (
    <div className="space-y-6">
      {/* Per-Customer RevShare */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.revshareProKunde')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-default)]">
                <th className="py-2 px-3 text-left font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('cm.customer')}</th>
                <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('cm.orders')}</th>
                <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">Handling</th>
                <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.fcAnteil')}</th>
                <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.wsAnteil')}</th>
                <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.fcQuote')}</th>
              </tr>
            </thead>
            <tbody>
              {kunden.sort((a, b) => b.revshare_abgabe - a.revshare_abgabe).map(k => {
                const wsAnteil = (k.umsatz_handling || 0) - (k.revshare_abgabe || 0);
                const fcQuote = (k.umsatz_handling || 0) > 0 ? (k.revshare_abgabe / k.umsatz_handling * 100) : 0;
                return (
                  <tr key={k.kunde_id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] cursor-pointer"
                    onClick={() => { setExpandedKunde(k.kunde_id); setTab('kunden'); }}>
                    <td className="py-2 px-3 text-[var(--text-primary)]">{k.kunde_name}</td>
                    <td className="py-2 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmt(k.auftraege)}</td>
                    <td className="py-2 px-3 text-right text-[var(--text-primary)] tabular-nums">{fmtEur(k.umsatz_handling)}</td>
                    <td className="py-2 px-3 text-right text-[var(--text-primary)] tabular-nums font-medium">{fmtEur(k.revshare_abgabe)}</td>
                    <td className="py-2 px-3 text-right text-[var(--text-secondary)] tabular-nums">{fmtEur(wsAnteil)}</td>
                    <td className="py-2 px-3 text-right">
                      <span className="text-[var(--text-secondary)]">{fmt(fcQuote, 1)}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Per-Category RevShare (when a customer is expanded) */}
      {hasDetail && revshareByCategory.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('jvDb.revshareProKategorie')}</h3>
          <p className="text-xs text-[var(--text-tertiary)] mb-4">{kundeDetail.kunde?.name}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-default)]">
                  <th className="py-2 px-3 text-left font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.position')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('cm.revenue')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.fcAnteil')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.wsAnteil')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('cm.orders')}</th>
                </tr>
              </thead>
              <tbody>
                {revshareByCategory.map((cat, i) => (
                  <tr key={i} className="border-b border-[var(--border-subtle)]">
                    <td className="py-2 px-3 text-[var(--text-primary)]">{cat.name}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[var(--text-secondary)]">{fmtEur(cat.umsatz)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[var(--text-primary)] font-medium">{fmtEur(cat.fc_anteil)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[var(--text-secondary)]">{fmtEur(cat.ws_anteil)}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-[var(--text-tertiary)]">{fmt(cat.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!hasDetail && (
        <div className="text-center text-[var(--text-tertiary)] text-sm py-6">
          {t('jvDb.selectCustomerForCategories')}
        </div>
      )}

      {/* Horizontal bar chart: RevShare per customer */}
      <Card>
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.revshareNachKundeChart')}</h3>
        {kunden.length === 0 ? (
          <div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('common.no_data')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, kunden.length * 44)}>
            <BarChart data={kunden.sort((a, b) => b.revshare_abgabe - a.revshare_abgabe)} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis type="number" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : v} />
              <YAxis type="category" dataKey="kunde_name" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} width={110} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v, name) => [fmtEur(v), name === 'revshare_abgabe' ? t('jvDb.fcAnteil') : t('jvDb.wsAnteil')]} />
              <Legend />
              <Bar dataKey="revshare_abgabe" name={t('jvDb.fcAnteil')} fill="#338edc" stackId="a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="ws_anteil_calc" name={t('jvDb.wsAnteil')} fill="#22D3EE" stackId="a" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

/* ─── KPI-Analyse Tab ────────────────────────────────── */
function KpiAnalyseTab({ data, t }) {
  const kunden = data?.kunden || [];
  const totals = data?.totals || {};

  if (kunden.length === 0) return <div className="text-center text-[var(--text-tertiary)] py-12">{t('common.no_data')}</div>;

  // Averages
  const avgAov = totals.auftraege > 0 ? totals.umsatz / totals.auftraege : 0;
  const avgFcQuote = kunden.reduce((s, k) => {
    const q = (k.umsatz_handling || 0) > 0 ? (k.revshare_abgabe / k.umsatz_handling * 100) : 0;
    return s + q;
  }, 0) / kunden.length;

  // Margin distribution buckets
  const margenBuckets = [
    { label: '< 0%', min: -Infinity, max: 0 },
    { label: '0–10%', min: 0, max: 10 },
    { label: '10–15%', min: 10, max: 15 },
    { label: '15–25%', min: 15, max: 25 },
    { label: '> 25%', min: 25, max: Infinity },
  ];
  const margenData = margenBuckets.map(b => ({
    label: b.label,
    count: kunden.filter(k => k.marge_pct >= b.min && k.marge_pct < b.max).length,
  }));

  // Order distribution (by order count)
  const orderBuckets = [
    { label: '< 50', min: 0, max: 50 },
    { label: '50–200', min: 50, max: 200 },
    { label: '200–500', min: 200, max: 500 },
    { label: '500–1K', min: 500, max: 1000 },
    { label: '> 1K', min: 1000, max: Infinity },
  ];
  const orderData = orderBuckets.map(b => ({
    label: b.label,
    count: kunden.filter(k => k.auftraege >= b.min && k.auftraege < b.max).length,
  }));

  // SOP comparison
  const sopKunden = kunden.filter(k => k.sop_pct > 0);
  const nonSopKunden = kunden.filter(k => k.sop_pct === 0);
  const sopAvgMarge = sopKunden.length > 0 ? sopKunden.reduce((s, k) => s + k.marge_pct, 0) / sopKunden.length : 0;
  const nonSopAvgMarge = nonSopKunden.length > 0 ? nonSopKunden.reduce((s, k) => s + k.marge_pct, 0) / nonSopKunden.length : 0;
  const sopAvgAov = sopKunden.length > 0 ? sopKunden.reduce((s, k) => s + (k.aov || 0), 0) / sopKunden.length : 0;
  const nonSopAvgAov = nonSopKunden.length > 0 ? nonSopKunden.reduce((s, k) => s + (k.aov || 0), 0) / nonSopKunden.length : 0;

  const sopCompare = [
    { label: t('jvDb.sopOrders'), marge: sopAvgMarge, aov: sopAvgAov, count: sopKunden.length },
    { label: t('jvDb.standardOrders'), marge: nonSopAvgMarge, aov: nonSopAvgAov, count: nonSopKunden.length },
  ];

  return (
    <div className="space-y-6">
      {/* KPI summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider mb-1">{t('jvDb.avgOrderValue')}</div>
          <div className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{fmtEur(avgAov)}</div>
        </Card>
        <Card>
          <div className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider mb-1">{t('jvDb.avgMarge')}</div>
          <div className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{fmt(totals.marge_pct, 1)}%</div>
        </Card>
        <Card>
          <div className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider mb-1">{t('jvDb.avgRevshareQuote')}</div>
          <div className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{fmt(avgFcQuote, 1)}%</div>
        </Card>
        <Card>
          <div className="text-xs text-[var(--text-tertiary)] uppercase tracking-wider mb-1">{t('jvDb.sopPct')}</div>
          <div className="text-xl font-bold text-[var(--text-primary)] tabular-nums">{fmt(totals.sop_pct, 1)}%</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Margin distribution histogram */}
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.margenVerteilung')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={margenData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="label" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v) => [v, t('jvDb.kundenCount')]} />
              <Bar dataKey="count" fill="#338edc" radius={[4, 4, 0, 0]} barSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Order distribution histogram */}
        <Card>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.orderVerteilung')}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={orderData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              <XAxis dataKey="label" tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fill: 'var(--text-tertiary)', fontSize: 12 }} />
              <ReTooltip contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border-default)', borderRadius: 8 }}
                formatter={(v) => [v, t('jvDb.kundenCount')]} />
              <Bar dataKey="count" fill="#22D3EE" radius={[4, 4, 0, 0]} barSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* SOP comparison */}
        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('jvDb.sopVergleich')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-default)]">
                  <th className="py-2 px-3 text-left font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('cm.type')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.kundenCount')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.avgMarge')}</th>
                  <th className="py-2 px-3 text-right font-semibold text-[var(--text-tertiary)] text-xs uppercase">{t('jvDb.avgOrderValue')}</th>
                </tr>
              </thead>
              <tbody>
                {sopCompare.map((row, i) => (
                  <tr key={i} className="border-b border-[var(--border-subtle)]">
                    <td className="py-2.5 px-3 text-[var(--text-primary)] font-medium">
                      {row.label}
                      {i === 0 && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] bg-cyan-500/15 text-cyan-600 font-medium">SOP</span>}
                    </td>
                    <td className="py-2.5 px-3 text-right text-[var(--text-secondary)] tabular-nums">{row.count}</td>
                    <td className="py-2.5 px-3 text-right">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${margeBadge(row.marge)}`}>
                        {fmt(row.marge, 1)}%
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-[var(--text-primary)] tabular-nums">{fmtEur(row.aov)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
